import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Leaf, Shield, Clock, Home as HomeIcon, Search, MousePointer, AlertTriangle, Bug, Layers, CircleDot, Triangle, Network, Wind, Hexagon, Bird, Droplet, Squirrel, Building } from 'lucide-react';
import { companyInfo, services } from '../mock';
import LeadForm from '../components/LeadForm';

const Services = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
            <Leaf size={16} style={{ color: 'var(--accent-text)' }} />
            <span className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>
              Eco-Friendly Solutions
            </span>
          </div>
          <h1 className="heading-1 mb-6">Professional Pest Control Services</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Comprehensive pest management solutions for residential and commercial properties across New York
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container">
          <div className="ai-grid">
            {services.map(service => {
              const IconComponent = service.icon === 'Search' ? Search :
                                   service.icon === 'MousePointer' ? MousePointer :
                                   service.icon === 'AlertTriangle' ? AlertTriangle :
                                   service.icon === 'Bug' ? Bug :
                                   service.icon === 'Layers' ? Layers :
                                   service.icon === 'CircleDot' ? CircleDot :
                                   service.icon === 'Triangle' ? Triangle :
                                   service.icon === 'Network' ? Network :
                                   service.icon === 'Wind' ? Wind :
                                   service.icon === 'Hexagon' ? Hexagon :
                                   service.icon === 'Bird' ? Bird :
                                   service.icon === 'Droplet' ? Droplet :
                                   service.icon === 'Home' ? HomeIcon :
                                   service.icon === 'Squirrel' ? Squirrel :
                                   service.icon === 'Building' ? Building : Shield;
              
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="product-card card-hover-lift"
                >
                  {/* Service Image */}
                  <div className="w-full h-48 mb-4 rounded-lg overflow-hidden" style={{ background: 'var(--bg-section)' }}>
                    <img 
                      src={service.image} 
                      alt={`${service.title} pest control service in NYC - Professional extermination`}
                      title={`${service.title} - NY Pest & Exterminating`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                      <IconComponent size={24} style={{ color: 'var(--accent-text)' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 mb-2">{service.title}</h3>
                      <p className="body-small">{service.description}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle size={16} style={{ color: 'var(--accent-text)' }} />
                        <span className="body-small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-4 flex items-center gap-2" style={{ color: 'var(--accent-text)' }}>
                    <span className="body-small font-semibold">View Details →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Process</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Simple, effective, and transparent
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <span className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>1</span>
              </div>
              <h3 className="heading-3 mb-2">Contact Us</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Call or request a free quote online
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <span className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>2</span>
              </div>
              <h3 className="heading-3 mb-2">Inspection</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Thorough assessment of your pest problem
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <span className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>3</span>
              </div>
              <h3 className="heading-3 mb-2">Treatment</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Eco-friendly solutions tailored to your needs
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <span className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>4</span>
              </div>
              <h3 className="heading-3 mb-2">Follow-Up</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Guaranteed results with free callbacks
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="heading-2 mb-4">Need Pest Control Now?</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Same-day service available for urgent situations
              </p>
            </div>
            <LeadForm 
              title="Request Your Free Quote" 
              subtitle="Tell us about your pest problem and a pest professional will contact you shortly"
              source="services_page"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;